import React, { useState } from "react";
import { Alert, Button, Card, Col, DatePicker, Form, Row, Select, Spin, Tabs } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { formatCents } from "./payrollOptions";
import { generalErrorAlert } from "../GeneralErrorAlert";
import "./Payroll.css";

import { useTranslation } from "react-i18next";
const { RangePicker } = DatePicker;

const request = async (path, { method = "post", body = null } = {}) => {
  const res = await axios({
    method,
    url: `${process.env.REACT_APP_BACKEND_URL}/payroll-reports/${path}`,
    data: body,
    headers: { Authorization: "Bearer " + localStorage.getItem("token") }
  });
  return res.data;
};

const Amount = ({ label, cents, strong }) => (
  <div className={`payroll-line${strong ? " payroll-line--strong" : ""}`}>
    <span className="payroll-line__label">{label}</span>
    <span className="payroll-line__value">{formatCents(cents)}</span>
  </div>
);

/** Everything that stops a filing from being submitted, shown before the figures. */
const Blockers = ({ blockers }) => {
  const { t } = useTranslation("dashboard");
  return blockers?.length ? (
    <Alert
      type="warning"
      showIcon
      className="mb-3"
      message={t("This filing is not ready yet")}
      description={
        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
          {blockers.map(blocker => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>
      }
    />
  ) : null;
};

/** The system computes the figures but does not transmit them; say so rather than imply it does. */
const TransmissionNotice = ({ transmission }) => {
  const { t } = useTranslation("dashboard");
  return transmission ? (
    <Alert
      type="info"
      showIcon
      className="mt-3"
      message={
        transmission.channel === "ELSTER"
          ? t(
              "These figures still have to be filed through ELSTER. No transmission is configured, so nothing has been sent."
            )
          : t(
              "These figures still have to be sent to the Krankenkasse through a certified Datenübermittlung. No transmission is configured, so nothing has been sent."
            )
      }
    />
  ) : null;
};

const LohnsteuerAnmeldung = () => {
  const { t } = useTranslation("dashboard");
  const [range, setRange] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!range?.[0] || !range?.[1]) return;
    setLoading(true);
    try {
      setData(
        await request("lohnsteuer-anmeldung", {
          body: { from: range[0].toISOString(), to: range[1].toISOString() }
        })
      );
    } catch (err) {
      generalErrorAlert(err);
    }
    setLoading(false);
  };

  return (
    <Spin spinning={loading}>
      <Row gutter={[16, 0]} align="bottom">
        <Col xs={24} md={12}>
          <Form.Item label={t("Reporting period")} className="payroll-reports__field">
            <RangePicker value={range} onChange={setRange} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6} lg={4}>
          <Form.Item className="payroll-reports__field">
            <Button type="primary" block onClick={run}>
              {t("Calculate")}
            </Button>
          </Form.Item>
        </Col>
      </Row>
      {data ? (
        <>
          <Blockers blockers={data.report.blockers} />
          <Card
            size="small"
            title={`${t("Lohnsteuer-Anmeldung")} · ${t("Employees")}: ${data.report.employeeCount}`}
          >
            <Amount label={t("Lohnsteuer")} cents={data.report.lohnsteuer} />
            <Amount label={t("Pauschale Lohnsteuer")} cents={data.report.pauschaleLohnsteuer} />
            <Amount label={t("Solidaritätszuschlag")} cents={data.report.solidaritaetszuschlag} />
            {Object.entries(data.report.churchTaxByDenomination || {}).map(([code, cents]) => (
              <Amount key={code} label={`${t("Kirchensteuer")} (${code})`} cents={cents} />
            ))}
            <Amount
              label={t("Total")}
              strong
              cents={
                data.report.lohnsteuer +
                data.report.solidaritaetszuschlag +
                data.report.kirchensteuer
              }
            />
          </Card>
          <TransmissionNotice transmission={data.transmission} />
        </>
      ) : null}
    </Spin>
  );
};

const Beitragsnachweis = () => {
  const { t } = useTranslation("dashboard");
  const [month, setMonth] = useState(dayjs());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!month) return;
    setLoading(true);
    try {
      setData(
        await request("beitragsnachweis", {
          body: { year: month.year(), month: month.month() + 1 }
        })
      );
    } catch (err) {
      generalErrorAlert(err);
    }
    setLoading(false);
  };

  return (
    <Spin spinning={loading}>
      <Row gutter={[16, 0]} align="bottom">
        <Col xs={24} md={12}>
          <Form.Item label={t("Month")} className="payroll-reports__field">
            <DatePicker
              picker="month"
              value={month}
              onChange={setMonth}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={6} lg={4}>
          <Form.Item className="payroll-reports__field">
            <Button type="primary" block onClick={run}>
              {t("Calculate")}
            </Button>
          </Form.Item>
        </Col>
      </Row>
      {data ? (
        <>
          <Blockers blockers={data.report.blockers} />
          {data.report.groups.map(group => (
            <Card
              key={group.betriebsnummer || group.krankenkasse || "unknown"}
              size="small"
              className="mb-3"
              title={`${group.krankenkasse || t("Unassigned")} · ${t("Employees")}: ${group.employeeCount}`}
            >
              <Amount label={t("Krankenversicherung")} cents={group.kv} />
              <Amount label={t("Pflegeversicherung")} cents={group.pv} />
              <Amount label={t("Rentenversicherung")} cents={group.rv} />
              <Amount label={t("Arbeitslosenversicherung")} cents={group.av} />
              <Amount label={t("Umlage U1")} cents={group.umlageU1} />
              <Amount label={t("Umlage U2")} cents={group.umlageU2} />
              <Amount label={t("Insolvenzgeldumlage (U3)")} cents={group.insolvenzgeldumlage} />
              <Amount label={t("Total")} cents={group.total} strong />
            </Card>
          ))}
          <TransmissionNotice transmission={data.transmission} />
        </>
      ) : null}
    </Spin>
  );
};

const Lohnsteuerbescheinigung = ({ employees = [] }) => {
  const { t } = useTranslation("dashboard");
  const [employeeId, setEmployeeId] = useState(null);
  const [year, setYear] = useState(dayjs());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!employeeId || !year) return;
    setLoading(true);
    try {
      setData(
        await request(`lohnsteuerbescheinigung/${employeeId}/${year.year()}`, { method: "get" })
      );
    } catch (err) {
      generalErrorAlert(err);
    }
    setLoading(false);
  };

  return (
    <Spin spinning={loading}>
      <Row gutter={[16, 0]} align="bottom">
        <Col xs={24} md={10}>
          <Form.Item label={t("Employee")} className="payroll-reports__field">
            <Select
              value={employeeId}
              onChange={setEmployeeId}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              placeholder={t("Select")}
              options={employees.map(employee => ({
                value: employee._id,
                label: `${employee.firstName} ${employee.lastName}`
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item label={t("Year")} className="payroll-reports__field">
            <DatePicker picker="year" value={year} onChange={setYear} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6} lg={4}>
          <Form.Item className="payroll-reports__field">
            <Button type="primary" block onClick={run}>
              {t("Calculate")}
            </Button>
          </Form.Item>
        </Col>
      </Row>
      {data ? (
        <>
          <Blockers blockers={data.report.blockers} />
          <Card size="small" title={`${t("Lohnsteuerbescheinigung")} ${data.report.year}`}>
            <Amount label={t("Bruttoarbeitslohn")} cents={data.report.bruttoarbeitslohn} />
            <Amount
              label={t("Steuerfreie Zuschläge (§ 3b)")}
              cents={data.report.steuerfreieZuschlaege}
            />
            <Amount label={t("Einbehaltene Lohnsteuer")} cents={data.report.lohnsteuer} />
            <Amount label={t("Solidaritätszuschlag")} cents={data.report.solidaritaetszuschlag} />
            <Amount label={t("Kirchensteuer")} cents={data.report.kirchensteuer} />
            <Amount
              label={t("AN-Anteil Krankenversicherung")}
              cents={data.report.arbeitnehmeranteil.kv}
            />
            <Amount
              label={t("AN-Anteil Pflegeversicherung")}
              cents={data.report.arbeitnehmeranteil.pv}
            />
            <Amount
              label={t("AN-Anteil Rentenversicherung")}
              cents={data.report.arbeitnehmeranteil.rv}
            />
            <Amount
              label={t("AG-Anteil Rentenversicherung")}
              cents={data.report.arbeitgeberanteil.rv}
            />
          </Card>
          <TransmissionNotice transmission={data.transmission} />
        </>
      ) : null}
    </Spin>
  );
};

/**
 * The three recurring German payroll filings. The figures are computed from the finalised
 * salary slips; transmission to ELSTER and to the Krankenkassen is a separate integration.
 */
const PayrollReports = ({ employees }) => {
  const { t } = useTranslation("dashboard");
  return (
    <Tabs
      className="payroll-reports"
      items={[
        { key: "lst", label: t("Lohnsteuer-Anmeldung"), children: <LohnsteuerAnmeldung /> },
        { key: "bnw", label: t("Beitragsnachweis"), children: <Beitragsnachweis /> },
        {
          key: "lstb",
          label: t("Lohnsteuerbescheinigung"),
          children: <Lohnsteuerbescheinigung employees={employees} />
        }
      ]}
    />
  );
};

export default PayrollReports;
